import { useState } from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useAuthStore } from '@/lib/auth-store'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import type { SessionUser } from '@/lib/session-types'

interface SwitchResponse {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

/**
 * Inline dropdown for switching the currently-selected artist.
 * Calls /auth/switch-artist which mints a fresh JWT scoped to the new artist.
 */
export function ArtistSwitcher() {
  const user = useAuthStore((s) => s.user)
  const setSession = useAuthStore((s) => s.setSession)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  if (!user || user.artistMemberships.length === 0) return null

  const current = user.artistMemberships.find((m) => m.artistId === user.artistId)

  async function onPick(artistId: string) {
    if (artistId === user!.artistId) {
      setOpen(false)
      return
    }
    setBusy(true)
    try {
      const data = await apiFetch<SwitchResponse>('/auth/switch-artist', {
        method: 'POST',
        body: { artistId },
      })
      setSession(data)
      setOpen(false)
    } catch (err) {
      console.error('switch-artist failed', err instanceof ApiClientError ? err.message : err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-ovation-ink/15 bg-white px-3 py-2 text-left text-sm hover:border-ovation-ink/30"
      >
        <div className="min-w-0">
          <div className="truncate font-medium">
            {current?.artistName ?? 'Pick artist'}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            {current?.role ?? '—'}
          </div>
        </div>
        <ChevronsUpDown className="h-4 w-4 text-ovation-muted" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-ovation-ink/15 bg-white shadow-lg">
          {user.artistMemberships.map((m) => (
            <button
              key={m.artistId}
              type="button"
              onClick={() => onPick(m.artistId)}
              className={cn(
                'flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-ovation-ink/5',
                m.artistId === user.artistId && 'bg-ovation-ink/5',
              )}
            >
              <div>
                <div>{m.artistName}</div>
                <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
                  {m.role}
                </div>
              </div>
              {m.artistId === user.artistId && (
                <Check className="h-4 w-4 text-emerald-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
